import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createResource, uploadFile, sendToGroup } from "../../services/postService";
import ReactQuill from "react-quill";
import imageCompression from "browser-image-compression";
import "react-quill/dist/quill.snow.css";
import {
  FaFont,
  FaCamera,
  FaLink,
  FaQuoteLeft,
  FaTable,
  FaTrash,
  FaChevronUp,
  FaChevronDown,
  FaFile,
} from "react-icons/fa";
import { Image as LucideImage, LetterText } from "lucide-react";
import DashboardStatusGrid from "./DashboardStatusGrid";

const FOLDER_KEY = "AnkuaruResources";
const MAX_FILE_SIZE = 1024 * 1024;

function CreateResource() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    source: ""
  });

  // State for our body content blocks.
  // Each block is an object { id, type, content, [file] }.
  const [bodyBlocks, setBodyBlocks] = useState([]);
  // To toggle the display of block-addition options.
  const [showOptions, setShowOptions] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitError, setSubmitError] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [toast, setToast] = useState({ message: "", isError: false });
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [showSendToGroup, setShowSendToGroup] = useState(false);
  const [showGroupList, setShowGroupList] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);

  useEffect(() => {
    if (!toast.message) return;
    const timer = setTimeout(
      () => setToast({ message: "", isError: false }),
      3000
    );
    return () => clearTimeout(timer);
  }, [toast]);

  // Handle changes for title, source, and type fields.
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Add a new block of the specified type.
  const addNewBlock = (blockType) => {
    setBodyBlocks((prev) => [
      ...prev,
      { id: Date.now(), type: blockType, content: "" },
    ]);
    setShowOptions(false);
  };

  // Update a specific block's content.
  const updateBlockContent = (id, updatedContent) => {
    setBodyBlocks((prev) =>
      prev.map((block) =>
        block.id === id
          ? {
              ...block,
              content:
                block.type === "link"
                  ? { ...block.content, ...updatedContent }
                  : updatedContent,
            }
          : block
      )
    );
  };

  // Remove a block.
  const removeBlock = (id) => {
    setBodyBlocks((prev) => prev.filter((block) => block.id !== id));
    setUploadProgress((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setUploadStatus((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setUploadErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  // For image blocks: When a file is chosen, create a preview URL and store the file.
  const handleImageUpload = async (id, e) => {
    const file = e.target.files[0];
    if (file) {
      let imageFile = file;
      if (file.size > MAX_FILE_SIZE) {
        try {
          setUploadStatus((prev) => ({ ...prev, [id]: "compressing" }));
          setUploadProgress((prev) => ({ ...prev, [id]: 0 }));
          imageFile = await imageCompression(file, {
            maxSizeMB: 1,
            useWebWorker: true,
            onProgress: (percent) => {
              const mapped = Math.min(30, Math.round((percent / 100) * 30));
              setUploadProgress((prev) => ({ ...prev, [id]: mapped }));
            },
          });
        } catch (error) {
          setUploadErrors((prev) => ({
            ...prev,
            [id]: "Image compression failed. Please try another image.",
          }));
          setToast({
            message: "Image compression failed.",
            isError: true,
          });
          e.target.value = "";
          return;
        }
      }
      if (imageFile.size > MAX_FILE_SIZE) {
        setUploadErrors((prev) => ({
          ...prev,
          [id]: "Image is still larger than 1MB after compression.",
        }));
        setToast({
          message: "Image is still larger than 1MB after compression.",
          isError: true,
        });
        e.target.value = "";
        return;
      }
      setUploadErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setUploadProgress((prev) => ({ ...prev, [id]: 0 }));
      setUploadStatus((prev) => ({ ...prev, [id]: "uploading" }));
      const preview = URL.createObjectURL(imageFile);
      setBodyBlocks((prev) =>
        prev.map((block) =>
          block.id === id ? { ...block, content: preview, file: imageFile } : block
        )
      );
      try {
        const uploadResult = await uploadFile(imageFile, FOLDER_KEY, (progress) => {
          const mapped = 30 + Math.round((progress / 100) * 70);
          setUploadProgress((prev) => ({
            ...prev,
            [id]: mapped,
          }));
        });
        setBodyBlocks((prev) =>
          prev.map((block) =>
            block.id === id
              ? { ...block, content: uploadResult.filelink, file: undefined }
              : block
          )
        );
        setUploadStatus((prev) => ({ ...prev, [id]: "completed" }));
        setUploadProgress((prev) => ({ ...prev, [id]: 100 }));
        setToast({ message: "Upload completed.", isError: false });
      } catch (error) {
        setUploadStatus((prev) => ({ ...prev, [id]: "error" }));
        setUploadErrors((prev) => ({
          ...prev,
          [id]: "Upload failed. Please try again.",
        }));
        setToast({ message: "Upload failed. Please try again.", isError: true });
      }
    }
  };

  // For document blocks: When a file is chosen, store the file and show filename
  const handleDocumentUpload = async (id, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setUploadErrors((prev) => ({
          ...prev,
          [id]: "File size must be 1MB or smaller.",
        }));
        setToast({
          message: "File size must be 1MB or smaller.",
          isError: true,
        });
        e.target.value = "";
        return;
      }
      setUploadErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setUploadProgress((prev) => ({ ...prev, [id]: 0 }));
      setUploadStatus((prev) => ({ ...prev, [id]: "uploading" }));
      setBodyBlocks((prev) =>
        prev.map((block) =>
          block.id === id ? { ...block, content: URL.createObjectURL(file), file } : block
        )
      );
      try {
        const uploadResult = await uploadFile(file, FOLDER_KEY, (progress) => {
          setUploadProgress((prev) => ({
            ...prev,
            [id]: progress,
          }));
        });
        setBodyBlocks((prev) =>
          prev.map((block) =>
            block.id === id
              ? { ...block, content: uploadResult.filelink, file: undefined }
              : block
          )
        );
        setUploadStatus((prev) => ({ ...prev, [id]: "completed" }));
        setUploadProgress((prev) => ({ ...prev, [id]: 100 }));
        setToast({ message: "Upload completed.", isError: false });
      } catch (error) {
        setUploadStatus((prev) => ({ ...prev, [id]: "error" }));
        setUploadErrors((prev) => ({
          ...prev,
          [id]: "Upload failed. Please try again.",
        }));
        setToast({ message: "Upload failed. Please try again.", isError: true });
      }
    }
  };

  const clearInputs = () => {
    setFormData({
      title: "",
      source: "",
      type: "news",
    });
    setBodyBlocks([]);
  };

  // Conversion function: converts our bodyBlocks array to our desired JSON structure.
  // This function will be run AFTER any image uploads are complete.
  const convertBlocksToStructuredContent = (blocks) => {
    return blocks
      .map((block) => {
        switch (block.type) {
          case "text":
            return {
              type: "paragraph",
              // The rich text from ReactQuill is HTML formatted.
              text: block.content,
            };
          case "subTopic":
            return {
              type: "subTopic", // Save as type "subTopic"
              text: block.content,
            };
          case "image":
            return {
              type: "image",
              url: block.content, // content now holds the actual file link after upload.
              caption: "",
              alt: "",
            };
          case "link":
            return {
              type: "link",
              url: block.content,
            };
          case "quote":
            return {
              type: "quote",
              text: block.content,
              author: "",
            };
          case "table":
            return {
              type: "table",
              html: block.content, // Save the HTML markup of the table.
            };
          case "document":
            return {
              type: "document",
              url: block.content, // content holds the actual file link after upload
              name: block.file?.name || "",
            };
          default:
            return null;
        }
      })
      .filter(Boolean);
  };

  // Move a block up or down in the array
  const handleSendToGroup = async () => {
    try {
      if (!selectedGroup) return;
      
      const response = await sendToGroup(
        selectedPosts,
        "New resource available", // Default message
        selectedGroup.id
      );
      
      setSubmitMessage("Resource sent to group successfully!");
      setSubmitError(false);
      setShowGroupList(false);
      setSelectedGroup(null);
      setSelectedPosts([]);
      setShowSendToGroup(false);
    } catch (error) {
      setSubmitMessage("Error sending to group: " + error.message);
      setSubmitError(true);
    }
  };

  const handlePostSelection = (postId, isChecked) => {
    setSelectedPosts(prev => 
      isChecked 
        ? [...prev, postId] 
        : prev.filter(id => id !== postId)
    );
    setShowSendToGroup(selectedPosts.length > 0 || isChecked);
  };

  const moveBlock = (id, direction) => {
    setBodyBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === id);
      if (idx < 0) return prev;
      const swapWith = direction === "up" ? idx - 1 : idx + 1;
      if (swapWith < 0 || swapWith >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
      return next;
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return "";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const renderUploadStatus = (blockId) => {
    const status = uploadStatus[blockId];
    const progress = uploadProgress[blockId] ?? 0;
    const error = uploadErrors[blockId];

    return (
      <>
        {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
        {status && (
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded h-2">
              <div
                className={`h-2 rounded ${
                  status === "error" ? "bg-red-500" : "bg-green-500"
                }`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {status === "completed"
                ? "Upload complete"
                : status === "error"
                  ? "Upload failed"
                  : status === "compressing"
                    ? "Compressing..."
                    : status === "idle"
                    ? "Ready to upload"
                    : `Uploading... ${progress}%`}
            </p>
          </div>
        )}
      </>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const folderKey = FOLDER_KEY;
      const hasUploads = bodyBlocks.some(
        (block) =>
          (block.type === "image" || block.type === "document") && block.file
      );
      // Process image blocks: Upload each image file (if exists) and update its content.
      const processedBlocks = await Promise.all(
        bodyBlocks.map(async (block) => {
          if ((block.type === "image" || block.type === "document") && block.file) {
            // Upload the file and get the real URL
            setUploadStatus((prev) => ({ ...prev, [block.id]: "uploading" }));
            setUploadProgress((prev) => ({ ...prev, [block.id]: 0 }));
            try {
              const uploadResult = await uploadFile(
                block.file,
                folderKey,
                (progress) => {
                  setUploadProgress((prev) => ({
                    ...prev,
                    [block.id]: progress,
                  }));
                }
              );

              setUploadStatus((prev) => ({ ...prev, [block.id]: "completed" }));
              setUploadProgress((prev) => ({ ...prev, [block.id]: 100 }));

              // Assume uploadResult.filelink contains the actual file URL.
              return {
                ...block,
                content: uploadResult.filelink,
                file: undefined,
              };
            } catch (error) {
              setUploadStatus((prev) => ({ ...prev, [block.id]: "error" }));
              throw error;
            }
          }
          return block;
        })
      );
      if (hasUploads) {
        setToast({ message: "Upload completed.", isError: false });
      }

      const structuredContent =
        convertBlocksToStructuredContent(processedBlocks);
      // Save the structured content as JSON.
      await createResource(
        formData.title,
        JSON.stringify(structuredContent),
        formData.source
      );
      setSubmitMessage("Post created successfully!");
      setSubmitError(false);
      navigate("/ankuaru/previewresource");
    } catch (error) {
      setToast({
        message: "Upload failed. Please try again.",
        isError: true,
      });
      setSubmitMessage("Error creating post: " + error.message);
      setSubmitError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray">
      <DashboardStatusGrid />
      <div className="container mx-auto max-w-6xl py-10">
        <div className="bg-transparent rounded-lg ">
          <div className="flex justify-between items-center mb-8 border-b pb-3">
            <h1 className="text-3xl font-rthin">Create Resource</h1>
            {showSendToGroup && (
              <button
                type="button"
                onClick={() => setShowGroupList(true)}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Send to Group
              </button>
            )}
          </div>
          {toast.message && (
            <div
              className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded shadow z-50 ${
                toast.isError
                  ? "bg-red-100 border-red-400 text-red-700"
                  : "bg-green-100 border-green-400 text-green-700"
              }`}
            >
              {toast.message}
              <button
                onClick={() => setToast({ message: "", isError: false })}
                className="ml-4 font-bold"
              >
                ×
              </button>
            </div>
          )}

          {showGroupList && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg max-w-md w-full">
                <h2 className="text-xl font-bold mb-4">Select Group</h2>
                <div className="space-y-2 mb-4">
                  {groups.map(group => (
                    <div key={group.id} className="flex items-center">
                      <input
                        type="radio"
                        id={`group-${group.id}`}
                        name="group"
                        checked={selectedGroup?.id === group.id}
                        onChange={() => setSelectedGroup(group)}
                        className="mr-2"
                      />
                      <label htmlFor={`group-${group.id}`}>{group.name}</label>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowGroupList(false)}
                    className="px-4 py-2 border rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSendToGroup}
                    disabled={!selectedGroup}
                    className={`px-4 py-2 text-white rounded ${!selectedGroup ? 'bg-gray-400' : 'bg-blue-500 hover:bg-blue-600'}`}
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title Field */}
            <div>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="Title"
                className="w-full px-3 py-2 border-b focus:outline-none focus:border-b-gray-600 bg-gray text-3xl font-rlight"
              />
            </div>

            {/* Source Field */}
            <div>
              <input
                type="text"
                id="source"
                name="source"
                value={formData.source}
                onChange={handleInputChange}
                required
                placeholder="Source"
                className="w-full px-3 py-2 border-b bg-gray text-xl font-rlight focus:outline-none focus:border-b-gray-600"
              />
            </div>

            <div>
              {/* Render already added blocks */}
              {bodyBlocks.map((block) => (
                <div
                  key={block.id}
                  className="relative border rounded-md p-4 mb-4 shadow-md"
                >
                  {/* ← move controls */}
                  <div className="absolute right-0 top-0 flex flex-col items-center pl-5 pt-2 space-y-1">
                    <button
                      type="button"
                      onClick={() => moveBlock(block.id, "up")}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <FaChevronUp />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveBlock(block.id, "down")}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <FaChevronDown />
                    </button>
                  </div>
                  <div className="absolute left-0 top-0 h-full w-10 flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => removeBlock(block.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <FaTrash size={20} />
                    </button>
                  </div>
                  {/* To keep a consistent layout, you can leave space on the left of each block */}
                  <div className="ml-16">
                    {block.type === "text" && (
                      <ReactQuill
                        value={block.content}
                        onChange={(content) =>
                          updateBlockContent(block.id, content)
                        }
                        modules={{
                          toolbar: [
                            [{ header: [1, 2, 3, false] }],
                            ["bold", "italic", "underline", "link"],
                            [{ list: "ordered" }, { list: "bullet" }],
                            ["clean"],
                          ],
                        }}
                      />
                    )}
                    {block.type === "subTopic" && (
                      // Render subTopic as an input styled as an H3.
                      <input
                        type="text"
                        placeholder="Sub Topic"
                        value={block.content}
                        onChange={(e) =>
                          updateBlockContent(block.id, e.target.value)
                        }
                        className="w-full px-3  font-bold text-xl "
                      />
                    )}
                    {block.type === "table" && (
                      <div
                        className="w-full p-2 border rounded-md bg-gray-100"
                        contentEditable
                        onBlur={(e) =>
                          updateBlockContent(
                            block.id,
                            e.currentTarget.innerHTML
                          )
                        }
                        dangerouslySetInnerHTML={{ __html: block.content }}
                      />
                    )}
                    {block.type === "image" && (
                      <>
                        {block.content ? (
                          <div>
                            <img
                              src={block.content}
                              alt="Preview"
                              className="max-h-64"
                            />
                            {/^https?:\/\//i.test(block.content) && (
                              <a
                                href={block.content}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block mt-1 text-blue-500 hover:underline text-sm"
                              >
                                {block.content}
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => removeBlock(block.id)}
                              className="mt-2 text-red-500 text-sm"
                            >
                              Remove Image
                            </button>
                          </div>
                        ) : (
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(block.id, e)}
                          />
                        )}
                        {block.file && (
                          <p className="text-xs text-gray-500 mt-1">
                            Size: {formatFileSize(block.file.size)}
                          </p>
                        )}
                        {renderUploadStatus(block.id)}
                      </>
                    )}
                    {block.type === "link" && (
                      <div className="flex flex-col space-y-2">
                        <input
                          type="text"
                          placeholder="Display Text (e.g., Click here)"
                          value={block.content?.displayText || ""}
                          onChange={(e) =>
                            updateBlockContent(block.id, {
                              displayText: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 text-lg border rounded-md bg-gray-50"
                        />
                        <input
                          type="text"
                          placeholder="Enter URL (e.g., https://example.com)"
                          value={block.content?.url || ""}
                          onChange={(e) =>
                            updateBlockContent(block.id, {
                              url: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 text-lg border rounded-md bg-gray-50"
                        />
                      </div>
                    )}

                    {block.type === "quote" && (
                      <input
                        type="text"
                        placeholder="Enter quote here"
                        value={block.content}
                        onChange={(e) =>
                          updateBlockContent(block.id, e.target.value)
                        }
                        className="w-full px-3 py-2 text-lg border-b rounded-md bg-gray"
                      />
                    )}
                    {block.type === "document" && (
                      <div className="space-y-2">
                        <input
                          type="file"
                          id={`document-${block.id}`}
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                          onChange={(e) => handleDocumentUpload(block.id, e)}
                          className="hidden"
                        />
                        <label 
                          htmlFor={`document-${block.id}`}
                          className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 cursor-pointer"
                        >
                          <FaFile className="mr-2" />
                          {block.content ? 'Change Document' : 'Upload Document'}
                        </label>
                        {block.content && (
                          <div className="mt-2">
                            <a 
                              href={block.content} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:underline"
                            >
                              View Document: {block.file?.name || 'Document'}
                            </a>
                            <button
                              type="button"
                              onClick={() => removeBlock(block.id)}
                              className="ml-2 text-red-500 hover:text-red-700 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        )}
                        {block.file && (
                          <p className="text-xs text-gray-500">
                            Size: {formatFileSize(block.file.size)}
                          </p>
                        )}
                        {renderUploadStatus(block.id)}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* New block addition area */}
              <div className="relative border rounded-md p-4">
                {/* Fixed plus button on the left side */}
                <div className="absolute left-0 top-0 h-full flex">
                  <button
                    type="button"
                    onClick={() => setShowOptions(!showOptions)}
                    className="w-12 h-full flex items-center justify-center border border-dashed border-gray-400 text-2xl bg-green-500 text-white"
                  >
                    +
                  </button>
                </div>

                {/* Content area of the add block section – always shifted to the right */}
                <div className="ml-16">
                  {showOptions ? (
                    <div className="mt-2 flex space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          addNewBlock("text");
                          setShowOptions(false);
                        }}
                        className="px-4 py-2 bg-transparent text-gray-500 rounded"
                      >
                        <LetterText size={20} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          addNewBlock("subTopic");
                          setShowOptions(false);
                        }}
                        className="px-4  bg-transparent text-gray-500 rounded"
                      >
                        Sub
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          addNewBlock("table");
                          setShowOptions(false);
                        }}
                        className="px-4 bg-transparent text-gray-500 rounded"
                      >
                        <FaTable size={20} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          addNewBlock("image");
                          setShowOptions(false);
                        }}
                        className="px-4 py-2 bg-transparent text-gray-500 rounded"
                      >
                        <LucideImage size={20} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          addNewBlock("link");
                          setShowOptions(false);
                        }}
                        className="px-4 py-2 bg-transparent text-gray-500 rounded"
                      >
                        <FaLink size={20} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          addNewBlock("quote");
                          setShowOptions(false);
                        }}
                        className="px-4 py-2 bg-transparent text-gray-500 rounded"
                      >
                        <FaQuoteLeft size={20} />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          addNewBlock("document");
                          setShowOptions(false);
                        }}
                        className="px-4 py-2 bg-transparent text-gray-500 rounded hover:bg-gray-100"
                        title="Add Document"
                      >
                        <FaFile size={20} />
                        <span className="sr-only">Document</span>
                      </button>
                    </div>
                  ) : (
                    <p className="text-gray-400">Tap + to add a body block</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-start space-x-4 mt-5">
              <button
                type="submit"
                disabled={saving}
                className={`px-4 py-2 text-white rounded-md ${
                  saving
                    ? "bg-green-300 cursor-not-allowed"
                    : "bg-green-500 hover:bg-green-300"
                }`}
              >
                {saving ? "Creating..." : "Create Post"}
              </button>

              <button
                type="button"
                onClick={clearInputs}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md"
              >
                Clear Inputs
              </button>

              <button
                type="button"
                onClick={() => navigate("/ankuaru/preViewPosts")}
                className="px-4 py-2 text-white hover:bg-red-300 border border-gray-300 rounded-md bg-red-500"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateResource;
